import {
  dutchHolidays,
  localParts,
  parseCsv,
  safeWidgetConfig,
  vimeoId,
  youtubeId,
  type WidgetKind,
} from '@tbcast/shared';
import QRCode from 'qrcode';
import { webRenderable } from '../engine/items';
import type { PlaybackContext, Renderable } from '../engine/types';
import { t } from '../i18n';
import * as store from '../store';
import { getWeather, weatherIcon } from './weather';

/** Root element for a widget that scales its typography with its own size (--u = 1% of the short side). */
function widgetRoot(
  kind: string,
  color?: string,
  background?: string,
  fontFamily?: string,
): { el: HTMLDivElement; destroyObserver: () => void } {
  const el = document.createElement('div');
  el.className = `tb-layer tb-widget tb-widget-${kind}`;
  if (color) el.style.color = color;
  if (background) el.style.background = background;
  if (fontFamily) el.style.fontFamily = `"${fontFamily}", Inter, system-ui, sans-serif`;
  const ro = new ResizeObserver(([e]) => {
    if (!e) return;
    const u = Math.min(e.contentRect.width, e.contentRect.height) / 100;
    el.style.setProperty('--u', `${u}px`);
    el.style.setProperty('--w', `${e.contentRect.width / 100}px`);
  });
  ro.observe(el);
  return { el, destroyObserver: () => ro.disconnect() };
}

function text(tag: string, cls: string, value: string): HTMLElement {
  const e = document.createElement(tag);
  e.className = cls;
  e.textContent = value;
  return e;
}

function fmt(locale: string, tz: string, d: Date, opts: Intl.DateTimeFormatOptions): string {
  try {
    return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'nl-NL', {
      timeZone: tz,
      ...opts,
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'nl-NL', opts).format(d);
  }
}

/** A widget renderable driven by a periodic update function. */
function periodic(
  el: HTMLElement,
  cleanup: () => void,
  intervalMs: number,
  update: () => void | Promise<void>,
  readyFn?: () => Promise<void>,
): Renderable {
  let timer: ReturnType<typeof setInterval> | null = null;
  return {
    el,
    async ready() {
      if (readyFn) await readyFn();
      await update();
    },
    play() {
      timer = setInterval(() => void update(), intervalMs);
    },
    destroy() {
      if (timer) clearInterval(timer);
      cleanup();
      el.remove();
    },
  };
}

export function createWidgetRenderable(
  type: string,
  rawConfig: Record<string, unknown>,
  ctx: PlaybackContext,
): Renderable {
  const kind = type as WidgetKind;
  const tz = ctx.timeZone;
  const loc = ctx.locale;
  switch (kind) {
    case 'clock': {
      const c = safeWidgetConfig('clock', rawConfig) ?? safeWidgetConfig('clock', {})!;
      const { el, destroyObserver } = widgetRoot('clock', c.color, c.background, c.fontFamily);
      const zone = c.timezone || tz;
      if (c.style === 'analog') {
        el.innerHTML = `<svg viewBox="0 0 200 200" class="tb-analog"><circle cx="100" cy="100" r="94" fill="none" stroke="currentColor" stroke-width="4" opacity=".9"/>${Array.from({ length: 12 }, (_, i) => `<line x1="100" y1="14" x2="100" y2="${i % 3 === 0 ? 30 : 22}" stroke="currentColor" stroke-width="${i % 3 === 0 ? 5 : 3}" stroke-linecap="round" transform="rotate(${i * 30} 100 100)"/>`).join('')}<line class="h" x1="100" y1="100" x2="100" y2="52" stroke="currentColor" stroke-width="7" stroke-linecap="round"/><line class="m" x1="100" y1="100" x2="100" y2="30" stroke="currentColor" stroke-width="5" stroke-linecap="round"/><line class="s" x1="100" y1="112" x2="100" y2="26" stroke="#06B6D4" stroke-width="2.5" stroke-linecap="round"/><circle cx="100" cy="100" r="6" fill="#06B6D4"/></svg>`;
        if (c.showDate) el.appendChild(text('div', 'tb-clock-date', ''));
        const h = el.querySelector<SVGLineElement>('.h')!;
        const m = el.querySelector<SVGLineElement>('.m')!;
        const s = el.querySelector<SVGLineElement>('.s')!;
        if (!c.showSeconds) s.style.display = 'none';
        const update = () => {
          const p = localParts(new Date(), zone);
          h.setAttribute('transform', `rotate(${(p.hour % 12) * 30 + p.minute / 2} 100 100)`);
          m.setAttribute('transform', `rotate(${p.minute * 6 + p.second / 10} 100 100)`);
          s.setAttribute('transform', `rotate(${p.second * 6} 100 100)`);
          const d = el.querySelector('.tb-clock-date');
          if (d)
            d.textContent = fmt(loc, zone, new Date(), {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            });
        };
        return periodic(el, destroyObserver, 1000, update);
      }
      const timeEl = text('div', 'tb-clock-time', '');
      el.appendChild(timeEl);
      const dateEl = c.showDate ? text('div', 'tb-clock-date', '') : null;
      if (dateEl) el.appendChild(dateEl);
      const update = () => {
        const now = new Date();
        timeEl.textContent = fmt(loc, zone, now, {
          hour: '2-digit',
          minute: '2-digit',
          ...(c.showSeconds ? { second: '2-digit' } : {}),
          hourCycle: 'h23',
        });
        if (dateEl)
          dateEl.textContent = fmt(loc, zone, now, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          });
      };
      return periodic(el, destroyObserver, 1000, update);
    }

    case 'date': {
      const c = safeWidgetConfig('date', rawConfig) ?? safeWidgetConfig('date', {})!;
      const { el, destroyObserver } = widgetRoot('date', c.color, c.background, c.fontFamily);
      const main = text('div', 'tb-date-main', '');
      const sub = text('div', 'tb-date-sub', '');
      el.append(main, sub);
      const update = () => {
        const now = new Date();
        if (c.format === 'weekday') {
          main.textContent = fmt(loc, tz, now, { weekday: 'long' });
          sub.textContent = fmt(loc, tz, now, { day: 'numeric', month: 'long' });
        } else if (c.format === 'short') {
          main.textContent = fmt(loc, tz, now, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
          sub.textContent = '';
        } else {
          main.textContent = fmt(loc, tz, now, { day: 'numeric', month: 'long' });
          sub.textContent = fmt(loc, tz, now, { weekday: 'long', year: 'numeric' });
        }
      };
      return periodic(el, destroyObserver, 30_000, update);
    }

    case 'weather': {
      const c = safeWidgetConfig('weather', rawConfig) ?? safeWidgetConfig('weather', {})!;
      const { el, destroyObserver } = widgetRoot('weather', c.color, c.background, c.fontFamily);
      const lat = c.lat ?? ctx.manifest.screen.location?.lat ?? 52.09;
      const lng = c.lng ?? ctx.manifest.screen.location?.lng ?? 5.12;
      const update = async () => {
        const w = await getWeather(lat, lng, tz);
        el.replaceChildren();
        if (!w) {
          el.appendChild(text('div', 'tb-weather-na', t(loc, 'weatherUnavailable')));
          return;
        }
        const conv = (v: number) => Math.round(c.units === 'f' ? v * 1.8 + 32 : v);
        const now = document.createElement('div');
        now.className = 'tb-weather-now';
        now.innerHTML = `<span class="tb-weather-icon">${weatherIcon(w.code, w.isDay)}</span>`;
        now.append(text('span', 'tb-weather-temp', `${conv(w.temp)}°`));
        el.appendChild(now);
        if (c.locationName) el.appendChild(text('div', 'tb-weather-loc', c.locationName));
        if (c.days > 0) {
          const row = document.createElement('div');
          row.className = 'tb-weather-days';
          for (const d of w.daily.slice(1, 1 + c.days)) {
            const cell = document.createElement('div');
            cell.append(
              text(
                'span',
                'tb-weather-day',
                fmt(loc, tz, new Date(`${d.date}T12:00:00Z`), { weekday: 'short' }),
              ),
            );
            const icon = document.createElement('span');
            icon.className = 'tb-weather-icon-sm';
            icon.innerHTML = weatherIcon(d.code, true);
            cell.append(
              icon,
              text('span', 'tb-weather-range', `${conv(d.max)}° / ${conv(d.min)}°`),
            );
            row.appendChild(cell);
          }
          el.appendChild(row);
        }
      };
      return periodic(el, destroyObserver, 15 * 60_000, update);
    }

    case 'rss': {
      const c = safeWidgetConfig('rss', rawConfig);
      const { el, destroyObserver } = widgetRoot('rss', c?.color, c?.background, c?.fontFamily);
      if (!c) return periodic(el, destroyObserver, 60_000, () => undefined);
      let items: Array<{ title: string; description: string }> = [];
      let rotate: ReturnType<typeof setInterval> | null = null;
      let pos = 0;
      const render = () => {
        el.replaceChildren();
        if (c.title) el.appendChild(text('div', 'tb-rss-title', c.title));
        if (!items.length) {
          el.appendChild(text('div', 'tb-rss-empty', t(loc, 'feedUnavailable')));
          return;
        }
        if (c.mode === 'ticker') {
          el.classList.add('tb-ticker');
          const track = document.createElement('div');
          track.className = 'tb-ticker-track';
          const line = items.map((i) => i.title).join('   •   ');
          track.append(text('span', '', line), text('span', '', `   •   ${line}`));
          el.appendChild(track);
          startMarquee(track, 120);
        } else if (c.mode === 'single') {
          const i = items[pos % items.length]!;
          el.append(text('div', 'tb-rss-headline', i.title));
          if (c.showDescription && i.description)
            el.append(text('div', 'tb-rss-desc', i.description));
        } else {
          const ul = document.createElement('ul');
          ul.className = 'tb-rss-list';
          for (const i of items.slice(0, c.maxItems)) {
            const li = document.createElement('li');
            li.append(text('div', 'tb-rss-headline', i.title));
            if (c.showDescription && i.description)
              li.append(text('div', 'tb-rss-desc', i.description));
            ul.appendChild(li);
          }
          el.appendChild(ul);
        }
      };
      const update = async () => {
        try {
          const xml = await ctx.api.feed(c.url);
          items = parseFeed(xml).slice(0, c.maxItems);
          await cacheFeed(c.url, items);
        } catch {
          items = (await cachedFeed(c.url)) ?? items;
        }
        render();
      };
      const r = periodic(
        el,
        () => {
          destroyObserver();
          if (rotate) clearInterval(rotate);
        },
        c.refreshMinutes * 60_000,
        update,
      );
      const origPlay = r.play.bind(r);
      r.play = () => {
        origPlay();
        if (c.mode === 'single') {
          rotate = setInterval(() => {
            pos++;
            render();
          }, c.secondsPerItem * 1000);
        }
      };
      return r;
    }

    case 'ticker': {
      const c = safeWidgetConfig('ticker', rawConfig) ?? safeWidgetConfig('ticker', { text: ' ' })!;
      const { el, destroyObserver } = widgetRoot('ticker', c.color, c.background, c.fontFamily);
      el.classList.add('tb-ticker');
      const track = document.createElement('div');
      track.className = 'tb-ticker-track';
      track.style.fontSize = `${c.fontSize}px`;
      const line = c.text.split('\n').filter(Boolean).join(c.separator);
      track.append(text('span', '', line + c.separator), text('span', '', line + c.separator));
      el.appendChild(track);
      let stop: (() => void) | null = null;
      return {
        el,
        async ready() {},
        play() {
          stop = startMarquee(track, c.speed * ctx.speed);
        },
        destroy() {
          stop?.();
          destroyObserver();
          el.remove();
        },
      };
    }

    case 'countdown': {
      const c = safeWidgetConfig('countdown', rawConfig);
      const { el, destroyObserver } = widgetRoot(
        'countdown',
        c?.color,
        c?.background,
        c?.fontFamily,
      );
      if (!c) return periodic(el, destroyObserver, 60_000, () => undefined);
      if (c.title) el.appendChild(text('div', 'tb-countdown-title', c.title));
      const grid = document.createElement('div');
      grid.className = 'tb-countdown-grid';
      el.appendChild(grid);
      const target = new Date(c.target).getTime();
      const labels =
        loc === 'en' ? ['days', 'hours', 'min', 'sec'] : ['dagen', 'uur', 'min', 'sec'];
      const update = () => {
        const diff = Math.max(0, target - Date.now());
        grid.replaceChildren();
        if (diff === 0) {
          grid.appendChild(text('div', 'tb-countdown-done', c.doneText || t(loc, 'countdownDone')));
          return;
        }
        const parts = [
          Math.floor(diff / 86_400_000),
          Math.floor(diff / 3_600_000) % 24,
          Math.floor(diff / 60_000) % 60,
          Math.floor(diff / 1000) % 60,
        ];
        parts.forEach((v, i) => {
          if (i === 3 && !c.showSeconds) return;
          const cell = document.createElement('div');
          cell.append(
            text('span', 'tb-countdown-num', String(v).padStart(2, '0')),
            text('span', 'tb-countdown-label', labels[i]!),
          );
          grid.appendChild(cell);
        });
      };
      return periodic(el, destroyObserver, 1000, update);
    }

    case 'qr': {
      const c = safeWidgetConfig('qr', rawConfig);
      const { el, destroyObserver } = widgetRoot('qr', c?.color, c?.background);
      return {
        el,
        async ready() {
          if (!c) return;
          const img = document.createElement('img');
          img.className = 'tb-qr-img';
          img.alt = c.label ?? 'QR';
          img.src = await QRCode.toDataURL(c.value, {
            margin: 1,
            width: 800,
            color: { dark: c.color, light: c.background },
          });
          el.appendChild(img);
          if (c.label) el.appendChild(text('div', 'tb-qr-label', c.label));
        },
        play() {},
        destroy() {
          destroyObserver();
          el.remove();
        },
      };
    }

    case 'webpage':
    case 'dashboard-url': {
      const c = safeWidgetConfig(kind, rawConfig);
      if (!c) return emptyWidget();
      return webRenderable(c.url, ctx, { zoom: c.zoom, refreshSec: c.refreshSec });
    }
    case 'social-wall': {
      const c = safeWidgetConfig('social-wall', rawConfig);
      if (!c) return emptyWidget();
      return webRenderable(c.url, ctx, { refreshSec: c.refreshSec });
    }
    case 'google-slides': {
      const c = safeWidgetConfig('google-slides', rawConfig);
      if (!c) return emptyWidget();
      // Published Google Slides: /pub → /embed with autoplay + loop.
      const u = new URL(c.url);
      u.pathname = u.pathname.replace(/\/(pub|edit|present)$/, '/embed');
      u.searchParams.set('start', 'true');
      u.searchParams.set('loop', 'true');
      u.searchParams.set('delayms', String(c.delayMs));
      u.searchParams.set('rm', 'minimal');
      return webRenderable(u.toString(), ctx);
    }
    case 'youtube': {
      const c = safeWidgetConfig('youtube', rawConfig);
      const id = c ? youtubeId(c.url) : null;
      if (!c || !id) return emptyWidget();
      const params = new URLSearchParams({
        autoplay: '1',
        mute: c.mute ? '1' : '0',
        controls: '0',
        rel: '0',
        modestbranding: '1',
        playsinline: '1',
        cc_load_policy: c.captions ? '1' : '0',
        ...(c.loop ? { loop: '1', playlist: id } : {}),
      });
      return webRenderable(`https://www.youtube-nocookie.com/embed/${id}?${params}`, ctx);
    }
    case 'vimeo': {
      const c = safeWidgetConfig('vimeo', rawConfig);
      const id = c ? vimeoId(c.url) : null;
      if (!c || !id) return emptyWidget();
      const params = new URLSearchParams({
        autoplay: '1',
        muted: c.mute ? '1' : '0',
        loop: c.loop ? '1' : '0',
        background: '1',
        dnt: '1',
      });
      return webRenderable(`https://player.vimeo.com/video/${id}?${params}`, ctx);
    }

    case 'sheet-table': {
      const c = safeWidgetConfig('sheet-table', rawConfig);
      const { el, destroyObserver } = widgetRoot('table', c?.color, c?.background, c?.fontFamily);
      if (!c) return periodic(el, destroyObserver, 60_000, () => undefined);
      const update = async () => {
        let rows: string[][] | null = null;
        try {
          rows = parseCsv(await ctx.api.feed(c.url));
          await cacheFeed(c.url, rows);
        } catch {
          rows = (await cachedFeed<string[][]>(c.url)) ?? null;
        }
        el.replaceChildren();
        if (c.title) el.appendChild(text('div', 'tb-table-title', c.title));
        if (!rows?.length) return;
        const table = document.createElement('table');
        table.className = 'tb-table';
        const head: string[] | null = c.headerRow ? (rows[0] ?? null) : null;
        const body: string[][] = c.headerRow ? rows.slice(1) : rows;
        if (head) {
          const tr = table.createTHead().insertRow();
          for (const cell of head) {
            const th = document.createElement('th');
            th.textContent = cell;
            th.style.background = c.headerBackground;
            th.style.color = c.headerColor;
            tr.appendChild(th);
          }
        }
        const tbody = table.createTBody();
        for (const row of body.slice(0, c.maxRows)) {
          const tr = tbody.insertRow();
          for (const cell of row) tr.insertCell().textContent = cell;
        }
        el.appendChild(table);
      };
      return periodic(el, destroyObserver, c.refreshMinutes * 60_000, update);
    }

    case 'queue': {
      const c = safeWidgetConfig('queue', rawConfig) ?? safeWidgetConfig('queue', {})!;
      const { el, destroyObserver } = widgetRoot('queue', c.color, c.background, c.fontFamily);
      el.append(
        text('div', 'tb-queue-title', c.title || t(loc, 'nowServing')),
        text('div', 'tb-queue-number', `${c.prefix}${c.current}`),
      );
      if (c.counter)
        el.append(text('div', 'tb-queue-counter', `${t(loc, 'counter')} ${c.counter}`));
      return {
        el,
        ready: async () => undefined,
        play() {},
        destroy: () => (destroyObserver(), el.remove()),
      };
    }

    case 'menu': {
      const c = safeWidgetConfig('menu', rawConfig) ?? safeWidgetConfig('menu', {})!;
      const { el, destroyObserver } = widgetRoot('menu', c.color, c.background, c.fontFamily);
      el.style.setProperty('--accent', c.accent);
      el.appendChild(text('div', 'tb-menu-title', c.title));
      const grid = document.createElement('div');
      grid.className = 'tb-menu-grid';
      grid.style.gridTemplateColumns = `repeat(${c.columns}, minmax(0, 1fr))`;
      for (const section of c.sections) {
        const sec = document.createElement('section');
        sec.appendChild(text('h3', 'tb-menu-section', section.name));
        for (const item of section.items) {
          const row = document.createElement('div');
          row.className = 'tb-menu-item';
          const left = document.createElement('div');
          left.append(text('div', 'tb-menu-name', item.name));
          if (item.description) left.append(text('div', 'tb-menu-desc', item.description));
          row.append(left, text('div', 'tb-menu-price', `${c.currency} ${item.price}`));
          sec.appendChild(row);
        }
        grid.appendChild(sec);
      }
      el.appendChild(grid);
      return {
        el,
        ready: async () => undefined,
        play() {},
        destroy: () => (destroyObserver(), el.remove()),
      };
    }

    case 'holidays': {
      const c = safeWidgetConfig('holidays', rawConfig) ?? safeWidgetConfig('holidays', {})!;
      const { el, destroyObserver } = widgetRoot('holidays', c.color, c.background, c.fontFamily);
      const update = () => {
        const today = localParts(new Date(), tz).date;
        const y = Number(today.slice(0, 4));
        const end = new Date(new Date(`${today}T00:00:00Z`).getTime() + c.daysAhead * 86_400_000)
          .toISOString()
          .slice(0, 10);
        const events: Array<{ date: string; label: string; birthday: boolean }> = [];
        if (c.showHolidays && c.country === 'NL')
          for (const yy of [y, y + 1])
            for (const h of dutchHolidays(yy))
              events.push({ date: h.date, label: loc === 'en' ? h.en : h.nl, birthday: false });
        for (const b of c.birthdays)
          for (const yy of [y, y + 1])
            events.push({ date: `${yy}-${b.date}`, label: b.name, birthday: true });
        const upcoming = events
          .filter((e) => e.date >= today && e.date <= end)
          .sort((a, b) => a.date.localeCompare(b.date))
          .slice(0, 8);
        el.replaceChildren(text('div', 'tb-holidays-title', c.title));
        const ul = document.createElement('ul');
        ul.className = 'tb-holidays-list';
        for (const e of upcoming) {
          const li = document.createElement('li');
          const days = Math.round(
            (new Date(`${e.date}T00:00:00Z`).getTime() - new Date(`${today}T00:00:00Z`).getTime()) /
              86_400_000,
          );
          const when =
            days === 0
              ? t(loc, 'today')
              : days === 1
                ? t(loc, 'tomorrow')
                : fmt(loc, 'UTC', new Date(`${e.date}T12:00:00Z`), {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                  });
          li.append(
            text('span', 'tb-holidays-icon', e.birthday ? '🎂' : '📅'),
            text(
              'span',
              'tb-holidays-label',
              e.birthday ? `${t(loc, 'birthday')}: ${e.label}` : e.label,
            ),
            text('span', 'tb-holidays-when', when),
          );
          if (days === 0) li.classList.add('is-today');
          ul.appendChild(li);
        }
        el.appendChild(ul);
      };
      return periodic(el, destroyObserver, 10 * 60_000, update);
    }

    case 'html': {
      const c = safeWidgetConfig('html', rawConfig);
      if (!c) return emptyWidget();
      const el = document.createElement('div');
      el.className = 'tb-layer';
      const iframe = document.createElement('iframe');
      iframe.className = 'tb-fill';
      // No allow-same-origin: custom HTML can never touch the player, its storage or the device token.
      iframe.setAttribute('sandbox', c.allowScripts ? 'allow-scripts' : '');
      iframe.referrerPolicy = 'no-referrer';
      iframe.srcdoc = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: data:; style-src 'unsafe-inline' https:; font-src https: data:; media-src https:;${c.allowScripts ? " script-src 'unsafe-inline';" : ''}"><style>html,body{margin:0;height:100%;overflow:hidden}${c.css}</style></head><body>${c.html}</body></html>`;
      el.appendChild(iframe);
      return {
        el,
        ready: async () => undefined,
        play() {},
        destroy: () => (iframe.remove(), el.remove()),
      };
    }
  }
  return emptyWidget();
}

function emptyWidget(): Renderable {
  const el = document.createElement('div');
  el.className = 'tb-layer';
  return { el, ready: async () => undefined, play() {}, destroy: () => el.remove() };
}

/** Smooth, frame-based marquee (no layout thrash; pauses when hidden). */
function startMarquee(track: HTMLElement, pxPerSec: number): () => void {
  let x = 0;
  let last = performance.now();
  let raf = 0;
  const step = (now: number) => {
    const dt = Math.min(100, now - last);
    last = now;
    const half = track.scrollWidth / 2;
    x -= (pxPerSec * dt) / 1000;
    if (half > 0 && -x >= half) x += half;
    track.style.transform = `translate3d(${x}px,0,0)`;
    raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);
  return () => cancelAnimationFrame(raf);
}

export function parseFeed(xml: string): Array<{ title: string; description: string }> {
  const doc = new DOMParser().parseFromString(xml, 'text/xml');
  const nodes = [...doc.querySelectorAll('item, entry')];
  const strip = (s: string) => {
    const d = new DOMParser().parseFromString(s, 'text/html');
    return (d.body.textContent ?? '').replace(/\s+/g, ' ').trim();
  };
  // Some XML parsers expose CDATA sections only through innerHTML.
  const textOf = (el: Element | null) =>
    el ? el.textContent?.trim() || (el.innerHTML ?? '').replace(/<!\[CDATA\[|\]\]>/g, '') : '';
  return nodes
    .map((n) => ({
      title: strip(textOf(n.querySelector('title'))),
      description: strip(textOf(n.querySelector('description, summary, content'))).slice(0, 280),
    }))
    .filter((i) => i.title);
}

async function cacheFeed(url: string, data: unknown): Promise<void> {
  await store.set(`feed:${url}`, data);
}
async function cachedFeed<T = Array<{ title: string; description: string }>>(
  url: string,
): Promise<T | undefined> {
  return store.get<T>(`feed:${url}`);
}
